import React, { useState, useEffect } from 'react';
import { 
  FaTrash, FaEnvelope, FaPhone, FaUser, FaComment, FaCalendar, 
  FaSearch, FaFilter, FaCheckSquare, FaRegSquare, FaCloudDownloadAlt,
  FaTimes, FaSort, FaSortUp, FaSortDown, FaGlobe, FaFlag
} from 'react-icons/fa';
import { CSVLink } from 'react-csv';
import axios from 'axios';

const ContactManagement = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedContact, setSelectedContact] = useState(null);
  
  // New States for Redesign
  const [showFilters, setShowFilters] = useState(false);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'desc' });
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
  });

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    const token = localStorage.getItem('adminToken');
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/contact/admin/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setContacts(response.data.data);
      } else {
        throw new Error('Failed to fetch contacts');
      }
    } catch (error) {
      setError(error.response?.data?.message || error.message || 'Failed to fetch contacts');
      console.error('Error fetching contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) return;

    const token = localStorage.getItem('adminToken');
    try {
      const response = await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/api/contact/admin/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        await fetchContacts();
        setError('');
        if (selectedIds.includes(id)) {
          setSelectedIds(selectedIds.filter(selectedId => selectedId !== id));
        }
      } else {
        throw new Error('Failed to delete contact');
      }
    } catch (error) {
      setError(error.response?.data?.message || error.message || 'Failed to delete contact');
    }
  };

  const handleBatchDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Are you sure you want to delete ${selectedIds.length} items?`)) return;

    const token = localStorage.getItem('adminToken');
    try {
      for (const id of selectedIds) {
        await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/api/contact/admin/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      await fetchContacts();
      setSelectedIds([]);
      setSelectionMode(false);
      setError('');
    } catch (error) {
      setError('Failed to complete batch delete');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const filteredContacts = React.useMemo(() => {
    return contacts
      .filter(contact => {
        const query = searchQuery.toLowerCase();
        const matchesSearch = 
          !searchQuery ||
          contact.name?.toLowerCase().includes(query) ||
          contact.email?.toLowerCase().includes(query) ||
          contact.phone?.toLowerCase().includes(query) ||
          contact.comment?.toLowerCase().includes(query);

        const submissionDate = new Date(contact.createdAt);
        const matchesStartDate = !filters.startDate || submissionDate >= new Date(filters.startDate);
        const matchesEndDate = !filters.endDate || submissionDate <= new Date(filters.endDate + 'T23:59:59');

        return matchesSearch && matchesStartDate && matchesEndDate;
      })
      .sort((a, b) => {
        if (!sortConfig.key) return 0;
        const valA = a[sortConfig.key] || '';
        const valB = b[sortConfig.key] || '';
        if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
        if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
  }, [contacts, searchQuery, filters, sortConfig]);

  const toggleSelectAll = () => {
    if (selectedIds.length === filteredContacts.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredContacts.map(r => r._id));
    }
  };

  const toggleSelectOne = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(i => i !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  const csvData = React.useMemo(() => {
    const dataToExport = selectedIds.length > 0 
      ? filteredContacts.filter(r => selectedIds.includes(r._id))
      : filteredContacts;
    
    return dataToExport.map(r => ({
      Name: r.name,
      Email: r.email,
      Phone: r.phone,
      Message: r.comment,
      SubmittedAt: formatDate(r.createdAt)
    }));
  }, [filteredContacts, selectedIds]);

  const SortIcon = ({ field }) => {
    if (sortConfig.key !== field) return <FaSort className="w-2.5 h-2.5 text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity" />;
    return sortConfig.direction === 'asc' ? <FaSortUp className="w-2.5 h-2.5 text-primary-500" /> : <FaSortDown className="w-2.5 h-2.5 text-primary-500" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary-100 border-t-primary-600 rounded-full animate-spin"></div>
          <p className="text-gray-500 font-bold animate-pulse text-sm">Loading contacts...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pb-4 space-y-4">
      {/* Header and Filter Section */}
      <div className="px-4 lg:px-6 space-y-4">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Contact Form Submissions</h1>
            <p className="mt-1 text-gray-600 dark:text-gray-400 font-medium">View and manage all incoming inquiries from the contact form</p>
          </div>
          <div className="flex items-center gap-3">
             <span className="inline-flex items-center px-4 py-2 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 text-sm font-bold rounded-xl border border-primary-100 dark:border-primary-800">
              Total: {contacts.length} {contacts.length === 1 ? 'submission' : 'submissions'}
            </span>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-xl animate-in fade-in duration-300">
            {error}
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: 'Total Contacts', value: contacts.length, color: 'primary', icon: <FaUser className="text-primary-600" /> },
            { label: 'Today', value: contacts.filter(r => new Date(r.createdAt).toDateString() === new Date().toDateString()).length, color: 'green', icon: <FaCalendar className="text-green-600" /> },
            { label: 'With Phone', value: contacts.filter(r => r.phone).length, color: 'blue', icon: <FaPhone className="text-blue-600" /> },
            { label: 'Filtered', value: filteredContacts.length, color: 'purple', icon: <FaFilter className="text-purple-600" /> },
          ].map((stat, i) => (
            <div key={i} className="bg-white dark:bg-dark-800 rounded-2xl border border-gray-100 dark:border-dark-700 p-5 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest">{stat.label}</p>
                <div className={`p-2 rounded-lg bg-${stat.color}-50 dark:bg-${stat.color}-900/20`}>
                  {stat.icon}
                </div>
              </div>
              <p className="text-3xl font-black text-gray-900 dark:text-white">{stat.value}</p>
            </div>
          ))}
        </div>

        {/* Filter and Actions Bar */}
        <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-sm border border-gray-100 dark:border-dark-700 p-4">
          <div className="flex flex-col md:flex-row gap-4 items-center">
            <div className="relative flex-grow w-full">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-primary-500 w-4 h-4" />
              <input
                type="text"
                placeholder="Search by name, email, phone, or message..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-dark-700 border border-gray-100 dark:border-dark-600 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/50 transition-all font-medium text-sm"
              />
            </div>
            
            <div className="flex items-center gap-3 w-full md:w-auto">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl font-bold text-xs transition-all whitespace-nowrap border ${
                  showFilters 
                  ? "bg-primary-50 text-primary-600 border-primary-200" 
                  : "bg-white dark:bg-dark-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-dark-700 hover:bg-gray-50"
                }`}
              >
                <FaFilter className={showFilters ? "text-primary-600" : "text-gray-400"} />
                {showFilters ? "Hide Filters" : "Filters"}
              </button>

              <button
                onClick={() => setSelectionMode(!selectionMode)}
                className={`flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl font-bold text-xs transition-all whitespace-nowrap ${
                  selectionMode 
                  ? "bg-primary-600 text-white shadow-md shadow-primary-500/30" 
                  : "bg-gray-100 dark:bg-dark-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200"
                }`}
              >
                <FaCheckSquare className={selectionMode ? "animate-pulse" : ""} />
                {selectionMode ? "Selection Active" : "Selection Mode"}
              </button>
              
              <CSVLink
                data={csvData}
                filename={`Contacts_${new Date().toISOString().split('T')[0]}.csv`}
                className="flex items-center justify-center gap-3 px-5 py-2.5 bg-green-600 text-white rounded-xl font-bold text-xs hover:bg-green-700 transition-all shadow-md shadow-green-500/30 whitespace-nowrap"
              >
                <FaCloudDownloadAlt size={18} />
                Export CSV {selectedIds.length > 0 ? `(${selectedIds.length})` : ''}
              </CSVLink>

              {selectionMode && selectedIds.length > 0 && (
                <button
                  onClick={handleBatchDelete}
                  className="px-5 py-2.5 bg-red-600 text-white rounded-xl font-bold text-xs hover:bg-red-700 transition-all shadow-md shadow-red-500/30 whitespace-nowrap"
                >
                  Delete ({selectedIds.length})
                </button>
              )}
            </div>
          </div>

          {/* Advanced Filters Panel */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-100 dark:border-dark-700 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 animate-in fade-in slide-in-from-top-2 duration-300">
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest pl-1">From Date</label>
                <div className="relative">
                  <FaCalendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-3 h-3" />
                  <input
                    type="date"
                    value={filters.startDate}
                    onChange={(e) => setFilters({...filters, startDate: e.target.value})}
                    className="w-full bg-gray-50 dark:bg-dark-700 border border-gray-100 dark:border-dark-600 rounded-xl pl-9 pr-3 py-1.5 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-primary-500/50"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest pl-1">To Date</label>
                <div className="relative">
                  <FaCalendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-3 h-3" />
                  <input
                    type="date"
                    value={filters.endDate}
                    onChange={(e) => setFilters({...filters, endDate: e.target.value})}
                    className="w-full bg-gray-50 dark:bg-dark-700 border border-gray-100 dark:border-dark-600 rounded-xl pl-9 pr-3 py-1.5 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-primary-500/50"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Full Width Table Section */}
      <div className="bg-white dark:bg-dark-800 border-y border-gray-100 dark:border-dark-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse min-w-max">
            <thead className="bg-gray-50/50 dark:bg-dark-700/50 border-b border-gray-100 dark:border-dark-700">
              <tr>
                {selectionMode && (
                  <th className="px-6 py-4 text-center w-12">
                    <button
                      onClick={toggleSelectAll}
                      className="p-1.5 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                    >
                      {selectedIds.length === filteredContacts.length && filteredContacts.length > 0
                        ? <FaCheckSquare className="w-4 h-4" />
                        : <FaRegSquare className="w-4 h-4" />}
                    </button>
                  </th>
                )}
                <th className="px-6 py-4 text-left text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest cursor-pointer group" onClick={() => handleSort('name')}>
                  <div className="flex items-center gap-1.5">
                    Name 
                    <SortIcon field="name" />
                  </div>
                </th>
                <th className="px-6 py-4 text-left text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest cursor-pointer group" onClick={() => handleSort('email')}>
                  <div className="flex items-center gap-1.5">
                    Email 
                    <SortIcon field="email" />
                  </div>
                </th>
                <th className="px-6 py-4 text-left text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest">Phone</th>
                <th className="px-6 py-4 text-left text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest">Message</th>
                <th className="px-6 py-4 text-left text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest cursor-pointer group" onClick={() => handleSort('createdAt')}>
                  <div className="flex items-center gap-1.5">
                    Submitted 
                    <SortIcon field="createdAt" />
                  </div>
                </th>
                <th className="px-6 py-4 text-right text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest sticky right-0 bg-gray-50 dark:bg-dark-700 shadow-left">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50 dark:divide-dark-800">
              {filteredContacts.length === 0 ? (
                <tr>
                  <td colSpan={selectionMode ? 7 : 6} className="px-6 py-16 text-center">
                    <div className="flex flex-col items-center gap-2">
                      <div className="p-4 bg-gray-50 dark:bg-dark-700 rounded-full">
                        <FaSearch className="w-8 h-8 text-gray-300" />
                      </div>
                      <p className="text-sm font-bold text-gray-500 dark:text-gray-400">
                        {searchQuery ? 'No matches found.' : 'No contact submissions found.'}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredContacts.map((contact) => (
                  <tr 
                    key={contact._id} 
                    className={`group transition-all duration-200 hover:bg-primary-50/20 dark:hover:bg-primary-900/10 cursor-pointer ${selectedIds.includes(contact._id) ? 'bg-primary-50/50 dark:bg-primary-900/20' : ''}`}
                    onClick={() => selectionMode ? toggleSelectOne(contact._id) : setSelectedContact(contact)}
                  >
                    {selectionMode && (
                      <td className="px-6 py-4 text-center" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => toggleSelectOne(contact._id)}
                          className={`p-1.5 rounded-lg transition-colors ${selectedIds.includes(contact._id) ? 'text-primary-600 bg-primary-100' : 'text-gray-400 hover:bg-gray-100'}`}
                        >
                          {selectedIds.includes(contact._id) ? <FaCheckSquare className="w-4 h-4" /> : <FaRegSquare className="w-4 h-4" />}
                        </button>
                      </td>
                    )}
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary-100 to-primary-50 dark:from-primary-900/30 dark:to-primary-900/10 flex items-center justify-center flex-shrink-0 border border-primary-200/50">
                          <FaUser className="text-primary-600 dark:text-primary-400 w-3.5 h-3.5" />
                        </div>
                        <span className="font-bold text-gray-900 dark:text-white text-sm">{contact.name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <a href={`mailto:${contact.email}`} className="text-primary-600 dark:text-primary-400 hover:underline text-xs font-bold" onClick={(e) => e.stopPropagation()}>
                        {contact.email}
                      </a>
                    </td>
                    <td className="px-6 py-4">
                      {contact.phone ? (
                        <div className="flex items-center gap-2">
                          <FaPhone className="text-gray-400 w-3 h-3" />
                          <span className="text-xs font-bold text-gray-700 dark:text-gray-300">{contact.phone}</span>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">Not provided</span>
                      )}
                    </td>
                    <td className="px-6 py-4 max-w-xs">
                       <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-1 font-medium italic">
                        "{contact.comment}"
                      </p>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 text-xs font-bold text-gray-600 dark:text-gray-400">
                        <FaCalendar className="w-3 h-3 text-gray-400" />
                        {formatDate(contact.createdAt)}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right sticky right-0 bg-white dark:bg-dark-800 group-hover:bg-primary-50/20 dark:group-hover:bg-primary-900/10 transition-colors shadow-left">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={(e) => { e.stopPropagation(); setSelectedContact(contact); }}
                          className="px-3 py-1.5 text-xs font-bold bg-gray-100 dark:bg-dark-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-dark-600"
                        >
                          View Details
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal for detail view */}
      {selectedContact && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setSelectedContact(null)}>
          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
             <div className="p-6 border-b border-gray-100 dark:border-dark-700 flex items-center justify-between bg-gray-50/50 dark:bg-dark-700/50">
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">Contact Inquiry</h2>
                <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-1 uppercase tracking-widest font-black">Submission Details</p>
              </div>
              <button
                onClick={() => setSelectedContact(null)}
                className="p-2 text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-white dark:hover:bg-dark-600 rounded-xl transition-all shadow-sm"
              >
                <FaTimes className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)] space-y-6">
              <div className="grid sm:grid-cols-2 gap-6">
                 <div className="bg-gray-50 dark:bg-dark-700/50 p-4 rounded-2xl border border-gray-100 dark:border-dark-700">
                  <label className="flex items-center gap-1.5 text-[10px] font-black text-gray-500 dark:text-gray-400 mb-2 uppercase tracking-widest">
                    <FaUser className="w-2.5 h-2.5" /> Full Name
                  </label>
                  <p className="text-gray-900 dark:text-white font-bold">{selectedContact.name}</p>
                </div>
                <div className="bg-gray-50 dark:bg-dark-700/50 p-4 rounded-2xl border border-gray-100 dark:border-dark-700">
                  <label className="flex items-center gap-1.5 text-[10px] font-black text-gray-500 dark:text-gray-400 mb-2 uppercase tracking-widest">
                    <FaEnvelope className="w-2.5 h-2.5" /> Email Address
                  </label>
                  <p className="text-gray-900 dark:text-white font-bold">{selectedContact.email}</p>
                </div>
                <div className="bg-gray-50 dark:bg-dark-700/50 p-4 rounded-2xl border border-gray-100 dark:border-dark-700">
                  <label className="flex items-center gap-1.5 text-[10px] font-black text-gray-500 dark:text-gray-400 mb-2 uppercase tracking-widest">
                    <FaPhone className="w-2.5 h-2.5" /> Phone Number
                  </label>
                  <p className="text-gray-900 dark:text-white font-bold">{selectedContact.phone || 'Not provided'}</p>
                </div>
                <div className="bg-gray-50 dark:bg-dark-700/50 p-4 rounded-2xl border border-gray-100 dark:border-dark-700">
                  <label className="flex items-center gap-1.5 text-[10px] font-black text-gray-500 dark:text-gray-400 mb-2 uppercase tracking-widest">
                    <FaCalendar className="w-2.5 h-2.5" /> Submitted On
                  </label>
                  <p className="text-gray-900 dark:text-white font-bold">{formatDate(selectedContact.createdAt)}</p>
                </div>
              </div>

              <div className="bg-gray-50 dark:bg-dark-700/50 p-5 rounded-2xl border border-gray-100 dark:border-dark-700">
                <label className="flex items-center gap-1.5 text-[10px] font-black text-gray-500 dark:text-gray-400 mb-3 uppercase tracking-widest">
                  <FaComment className="w-2.5 h-2.5" /> Message Contents
                </label>
                <div className="p-4 bg-white dark:bg-dark-800 rounded-xl border border-gray-100 dark:border-dark-700 shadow-inner">
                  <p className="text-gray-900 dark:text-white whitespace-pre-wrap text-sm leading-relaxed font-medium">{selectedContact.comment}</p>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-100 dark:border-dark-700 flex justify-end gap-3 bg-gray-50/50 dark:bg-dark-700/50">
              <button
                onClick={() => setSelectedContact(null)}
                className="px-6 py-2.5 bg-white dark:bg-dark-800 text-gray-700 dark:text-gray-300 rounded-xl font-bold text-xs hover:bg-gray-50 border border-gray-200 dark:border-dark-700 transition-all shadow-sm"
              >
                Close
              </button>
              <button
                onClick={() => { handleDelete(selectedContact._id); setSelectedContact(null); }}
                className="px-6 py-2.5 bg-red-600 text-white rounded-xl font-bold text-xs hover:bg-red-700 transition-all shadow-md shadow-red-500/30"
              >
                Delete Submission
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactManagement;
